import {ChangeEvent, useEffect, useRef, useState} from 'react';
import {
  FormHelperText,
  InputAdornment,
  TextField,
  Box,
  InputLabel,
  Typography,
  IconButton,
} from '@mui/material';
import Select, {SelectProps} from '@mui/material/Select';
import {ReactComponent as SearchIcon} from '../../../assests/svg/search-icon-right.svg';
import {ReactComponent as ExpandIcon} from '../../../assests/svg/down-triangle.svg';
import {t} from 'i18next';
import styles from '../aur-select-dropdown/style.module.scss';
import SelectedItem from './SelectedItem';
import AurLoadingIndicator from '../aur-loading-indicator/AurLoadingIndictor';

import {ReactComponent as ClearIcon} from '../../../assests/svg/clear-icon.svg';
import {useFieldFocusStyle} from '../../../hooks/utils/useFieldFocusStyle';
import {
  MenuProps,
  StyledFormControl,
} from '../aur-select-dropdown/AurSelectDropdown';

const SCROLL_BAR_STYLE_CLASS = 'aur-item-container-scroll'; // Scrollbar selector class
const ITEMS_WITHOUT_SCROLL = 5; // Default items/options panel height
const ITEM_HEIGHT = 56; // Single item/option height
const SCROLL_TIMEOUT = 100; // Scroll trigger timeout in milli second
const ITEM_PADDING = '16px'; // padding for selected items

export const DropdownIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <ExpandIcon
    {...props}
    aria-hidden="true"
    focusable="false"
  />
);

type AurSelectDropdownProps = {
  items: any[]; // All options/items
  itemKey: string; // Name of the unique property of item/option object
  itemDisplay: string; // Name of the "display text in options" property of item/option object
  showDropDownLabel?: boolean; // To show dropdown label
  dropdownLabel?: string; // Label of the dropdown
  search?: boolean;
  searchPlaceHolder?: string; // Search field place holder
  selectAllLabel?: string; // Select all checkbox label
  defaultSelection?: any; // Default selected items/options
  itemsCountwithNoScroll?: number; // Minimum items/option shows with no scrollbar appear
  disabled?: boolean;
  selectedValue: any;
  helperTextClass?: string;

  // For validation
  hasValidationError?: boolean;
  validationText?: string;
  helperText?: string;
  labelId?: string;
  dataTestId?: string;
  onSelectionChange: (selectedItems?: any) => void; // Called after items/options commit/add
  onClearClicked?: () => void; // Called after clearing all selected items/options
  onOpenDropdown?: () => void; // Called after dropdown onen
  onCloseDropdown?: () => void; // Called after dropdown close
  onSearch?: (searchTerm: any) => void;
  searchReqLoading?: boolean; // For show loader while input search api call running
  disablePortal?: boolean; // For configure the portal of poper for options
} & SelectProps;

/**
 * Custom multi select dropdown
 *
 * Important: the container around this component must have >= 16px left padding/margin [mui suggested standard]
 */
const AurSingleSelectDropdown = (props: AurSelectDropdownProps) => {
  const {
    items,
    itemKey,
    itemDisplay,
    showDropDownLabel = true,
    dropdownLabel,
    search = true,
    searchPlaceHolder = t('userHomePage.search'),
    itemsCountwithNoScroll = ITEMS_WITHOUT_SCROLL,
    disabled = false,
    selectedValue,
    hasValidationError = false,
    validationText = '',
    helperText = '',
    labelId,
    dataTestId,
    helperTextClass,
    onSelectionChange,
    onOpenDropdown,
    onCloseDropdown,
    onSearch,
    searchReqLoading,
    disablePortal = false,
    ...rest
  } = props;

  const [itemData, setItemData] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<any>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState(searchTerm);
  const menuItemRefs = useRef<{[key: string]: HTMLLIElement | null}>({});
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [searchFieldText, setSearchFieldText] = useState('');

  const [isExpanded, setIsExpanded] = useState(true);

  const {borderClass, focusHandler, mouseKeyDownHandler} = useFieldFocusStyle({
    tabFocusClass: styles.tabFocusedBorder,
    mouseFocusClass: styles.mouseFocusedBorder,
  });

  // Set items/options panel height
  let itemContainerMaxHeight = ITEM_HEIGHT * itemsCountwithNoScroll;
  if (itemData.length < itemsCountwithNoScroll)
    itemContainerMaxHeight = ITEM_HEIGHT * itemData.length;

  // Called for temporary selected/unselected
  const selectionChangeHandler = (data: any) => {
    const index = itemData.map((e) => e[itemKey]).indexOf(data[itemKey]);

    setSelectedItems(itemData[index]);
    onSelectionChange(itemData[index]);
    setIsExpanded(false);
    selectCloseHandler();
  };

  // Debounce the search query using useEffect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (onSearch) onSearch(debouncedQuery);
  }, [debouncedQuery]);

  const searchClearHandler = () => {
    setSearchFieldText('');
    setSearchTerm('');
    scrollToTargetItem(itemData[0]);
  };

  // Called every key press on search field
  const searchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    // On clear scroll to first item/option
    if (!event.target.value.length) scrollToTargetItem(itemData[0]);
    setSearchFieldText(event.target.value);
    const searchText = event.target.value.trim();
    setSearchTerm(searchText);
    if (!searchText) return;
    // Starts with
    let targetItem = itemData.find((item) =>
      item[itemDisplay]
        ?.toString()
        .toLowerCase()
        .startsWith(searchText.toLowerCase())
    );
    if (!targetItem) {
      // Contains with
      targetItem = itemData.find((item) =>
        item[itemDisplay]
          ?.toString()
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );
    }
    if (targetItem) scrollToTargetItem(targetItem);
  };

  // Scroll to the target item/option
  const scrollToTargetItem = (item: any) => {
    if (item) {
      setTimeout(() => {
        menuItemRefs.current[item[itemKey]]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, SCROLL_TIMEOUT);
    }
  };

  // Called after dropdown open
  const selectOpenHandler = () => {
    setIsDropdownOpen(true);
    if (onOpenDropdown) onOpenDropdown();
  };
  // Called after dropdown close
  const selectCloseHandler = () => {
    setIsDropdownOpen(false);
    setSearchFieldText('');
    if (onCloseDropdown) onCloseDropdown();
  };

  // Initial state setup
  useEffect(() => {
    setSelectedItems(selectedValue);
    setItemData(items);
  }, [items]);

  const renderEmptyListMsessage = () => {
    if (itemData.length === 0 && search) {
      return (
        <Typography
          aria-label={t('common.noResults')}
          data-testid="empty-message-lable"
          className="typo-text body3_size no-result-label"
        >
          {t('common.noResults')}
        </Typography>
      );
    }
  };

  const searchIconState = searchFieldText ? (
    <IconButton
      aria-label="clear"
      size="small"
      onClick={() => searchClearHandler()}
    >
      <ClearIcon aria-hidden="true" focusable="false" />
    </IconButton>
  ) : (
    <IconButton
      tabIndex={-1}
      size="small"
      disableFocusRipple
      disableRipple
      disabled
    >
      <SearchIcon aria-hidden="true" focusable={false} />
    </IconButton>
  );

  

  return (
    <StyledFormControl
      error={hasValidationError}
      variant="filled"
      fullWidth
      className={`${styles.multiSelectDropdownInput} ${borderClass}`}
    >
      {showDropDownLabel && (
        <InputLabel
          id={`aur-multiple-checkbox-label-${labelId}`}
          className={`${disabled ? 'Mui-disabled' : ''}`}
          shrink={!!selectedItems}
        >
          {dropdownLabel}
        </InputLabel>
      )}
      <Select
        fullWidth
        value={selectedItems}
        MenuProps={{...MenuProps, disablePortal}}
        IconComponent={DropdownIcon}
        labelId={
          showDropDownLabel && labelId
            ? `aur-multiple-checkbox-label-${labelId}`
            : undefined
        }
        disabled={disabled}
        open={isDropdownOpen}
        onOpen={selectOpenHandler}
        onClose={selectCloseHandler}
        data-testid={dataTestId}
        renderValue={() => <span>{selectedItems[itemDisplay]}</span>}
        inputProps={{
          'aria-label':
          selectedItems?.[itemDisplay] ||
          dropdownLabel ||
          t('common.selectOptions'),
        }}
        {...rest}
        sx={{
          '& .MuiSelect-select': {
            paddingRight: ITEM_PADDING,
            paddingLeft: ITEM_PADDING,
          },
        }}
        onFocus={focusHandler}
        onMouseDown={mouseKeyDownHandler}
      >
        <Box>
          {search && (
            <Box
              data-testid="search-container-id"
              onKeyDown={(e) => {
                if (e.key !== 'Escape') e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {search && (
                <Box className={`${styles.aurSelectSearchContainer}`}>
                  <TextField
                    fullWidth
                    onChange={searchChangeHandler}
                    id="outlined-basic"
                    placeholder={searchPlaceHolder}
                    variant="outlined"
                    className={`${styles.aurMultiSelectSearch}`}
                    value={searchFieldText}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {searchReqLoading ? (
                            <AurLoadingIndicator show size={24} />
                          ) : (
                            searchIconState
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          {itemData.length > 0 && (
            <Box
              sx={{
                // These styles are dynamic and based on items/options data
                height: `${itemData.length * ITEM_HEIGHT}px`,
                overflow: 'auto',
                maxHeight: `${itemContainerMaxHeight}px`,
              }}
              className={`${SCROLL_BAR_STYLE_CLASS} ${styles.aurItemContainerScrollbar}`}
            >
              {itemData.map((item) => {
                return (
                  <SelectedItem
                    ref={(el) => (menuItemRefs.current[item[itemKey]] = el)}
                    key={item[itemKey]}
                    itemData={item}
                    itemDisplay={itemDisplay}
                    onItemClick={selectionChangeHandler}
                    isExpanded={isExpanded}
                    isSelected={
                      selectedItems
                        ? selectedItems[itemKey] == item[itemKey]
                        : false
                    }
                  />
                );
              })}
            </Box>
          )}
          {renderEmptyListMsessage()}
        </Box>
      </Select>
      <Box className={helperTextClass}>
        <FormHelperText>
          {hasValidationError ? validationText : helperText}
        </FormHelperText>
      </Box>
    </StyledFormControl>
  );
};

export default AurSingleSelectDropdown;
